import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { Box, CircularProgress, Typography } from "@mui/material";
import { yupResolver } from "@hookform/resolvers/yup";
import isEmpty from "lodash/isEmpty";

//importing custom modules
import { Button } from "../../components";
import Input from "../../components/UserInput/Input";
import { operatingTheatreSchema } from "../../yup";

// importing styles
import style from "./operating-theatre.module.css";
import { colors } from "../../theme/colors";

// importing api
import {
  useUpdateOperatingTheatreMutation,
  useGetOperatingTheatreDetailsQuery,
} from "../../services/api";
import { LabelBox } from "../../components";
import { RootState, useAppSelector } from "../../store";

type FormState = typeof operatingTheatreSchema.__outputType;

/**
 * Renders the edit Operating Theatre form and manages update workflow.
 *
 * Returns:
 *   JSX.Element: Form UI for editing an existing operating theatre.
 *
 * Notes:
 *   Requires route param id; preloads data via RTK Query and uses operatingTheatreSchema for validation.
 */
const EditOperatingTheatre = () => {
  const { id } = useParams();
  const navigation = useNavigate();
  const [updateOperatingTheatre] = useUpdateOperatingTheatreMutation();

  const { userDetails } = useAppSelector(
    (state: RootState) => state.auth
  );

  // Get Operating Theatre Details mutation hook
  const {
    data: operatingTheatreDetails,
    isLoading,
    isFetching,
  } = useGetOperatingTheatreDetailsQuery(id ?? "");

  const [loading, setLoading] = useState<boolean>(false);
  const [message, setMessage] = useState<LabelMessageType | null>(null);
  
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<FormState>({
    resolver: yupResolver(operatingTheatreSchema),
    mode: "onChange",
  });

  // Pre-fill form when details load so edits start from server state; skips when details are empty.
  useEffect(() => {
    if (!isEmpty(operatingTheatreDetails)) {
      reset({
        name: operatingTheatreDetails?.name,
        room_no: (operatingTheatreDetails)?.room_no ?? "",
        floor: operatingTheatreDetails?.floor ?? "",
        building: operatingTheatreDetails?.building ?? "",
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [operatingTheatreDetails]);

  /**
   * Submits updates for an existing operating theatre to the API.
   *
   * Args:
   *   values (FormState): Validated form data for the operating theatre.
   *
   * Returns:
   *   Promise<void>: Resolves after the update mutation completes.
   *
   * Raises:
   *   Displays an error message when the update mutation fails.
   *
   * Notes:
   *   Expects a valid numeric id from route params; assumes userDetails provides health_org_id.
   */
  const onSubmit = async (values: FormState) => {
    try {
      setLoading(true);

      const payload: EditOperatingTheatrePayload = {
        id: parseInt(id ?? "0"),
        name: values?.name,
        health_org_id: userDetails?.health_org_id ?? 0,
        room_no: values?.room_no ?? "",
        floor: values?.floor ?? "",
        building: values?.building ?? "",
      };

      const result = await updateOperatingTheatre(payload).unwrap();
      setLoading(false);
      setMessage({
        type: "success",
        text: "Operating Theatre updated successfully.",
      });
      setTimeout(() => {
        navigation(-1);
      }, 1500);
    } catch (error: unknown) {
      const parsedError =
        typeof error === "object" && error !== null
          ? (error as { data?: { detail?: string } })
          : undefined;
      setMessage({
        type: "error",
        text:
          parsedError?.data?.detail?.toString() || "Please enter valid details",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box className={style.container}>
      <Box component="form" onSubmit={handleSubmit(onSubmit)}>
        {(isFetching || isLoading) && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: "60vh",
              width: "calc(100vw - 400px)",
              zIndex: 999,
              backgroundColor: "transparent",
              position: "absolute",
            }}
          >
            <CircularProgress size={40} />
          </Box>
        )}
        <Box>
          <Typography className={style.detailHeading}>
            Operating Theatre Details
          </Typography>
          <Box className={style.flexTop} sx={{ marginTop: "20px" }}>
            <Box className={style.row}>
              <Input
                placeholder="Theatre Name"
                label="Theatre Name"
                required
                register={register}
                name="name"
                errors={errors}
              />
              <Input
                placeholder="Room Number"
                label="Room Number"
                register={register}
                name="room_no"
                errors={errors}
              />
            </Box>
            <Box className={style.row}>
              <Input
                placeholder="Floor"
                label="Floor"
                register={register}
                name="floor"
                errors={errors}
              />
              <Input
                placeholder="Building"
                label="Building"
                register={register}
                name="building"
                errors={errors}
              />
            </Box>

            <Box className={style.row}></Box>
            {message && (
              <Box mt={2}>
                <LabelBox
                  boxProps={{
                    height: 50,
                    display: "flex",
                    alignItems: "center",
                  }}
                  data={message}
                  onClose={() => setMessage(null)}
                />
              </Box>
            )}
            <Box className={style.buttons}>
              <Button
                sx={{
                  width: "200px",
                  height: "55px",
                }}
                variant="outlined"
                onClick={() => navigation(-1)}
                label="Cancel"
              />
              <Button
                loading={loading}
                sx={{
                  width: "200px",
                  height: "55px",
                  ":hover": { backgroundColor: colors.primary },
                }}
                variant="contained"
                type="submit"
                label="Save"
              />
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default EditOperatingTheatre;
